import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { concat, Observable, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import * as lodash from 'lodash';

import {
  PersonProjectControllerService,
  PersonProjectWithRelations, Project,
  ProjectControllerService,
  ProjectUpdateDtoPartial
} from '@rappider/rappider-sdk';
import { BrowserStorageManagementService, NotificationService, PaginationService, SyncRemoteDataService, StoreKeys } from '@rappider/services';
import { PATH_DEFINITIONS } from '@rappider/shared/definitions';
import { environment } from 'apps/rappider/src/environments/environment';
import { Navigate } from 'libs/shared/src/lib/states/router/router.actions';
import { SelectActiveProject } from '../active-project-state/active-project.actions';
import * as ProjectActions from './project.actions';
import * as AdminProjectsActions from 'libs/admin-dashboard/src/lib/state/admin-projects/admin-projects.actions';
import * as AuthenticationActions from 'libs/authentication/src/lib/state/authentication.actions';
import { UserRoles } from 'libs/shared/src/lib/guards/admin-guard/admin-dashboard-roles.enum';
import { selectPublicProjects } from './project.selectors';


/* get active project state actions */
// import * as ActiveProjectActions from 'libs/project/src/lib/states/active-project-state/active-project.actions';

@Injectable()
export class ProjectEffects {
  constructor(
    private actions$: Actions,
    private projectApi: ProjectControllerService,
    private personProjectApi: PersonProjectControllerService,
    private store: Store<any>,
    private notificationService: NotificationService,
    private paginationService: PaginationService,
    private browserStorageManagementService: BrowserStorageManagementService,
    private syncRemoteDataService: SyncRemoteDataService
  ) { }


  getPersonProjects = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.GetProjects>(
      ProjectActions.ActionTypes.GetProjects,
      AuthenticationActions.ActionTypes.AcceptInvitationByTokenSuccessful
    ),
    mergeMap(() =>
      this.personProjectApi.find().pipe(
        map((personProjects: PersonProjectWithRelations[]) =>
          new ProjectActions.GetPersonProjectsSuccessful({ personProjects })
        ),
        catchError(error => {
          this.notificationService.createNotification(
            'error',
            'SHARED.ERROR',
            'SHARED.COULDNT_LOAD'
          );
          return [
            new ProjectActions.GetProjectsFailure({ payload: { error, key: 'GetProjectsFailure', timestamp: Date.now() } })
          ];
        })
      )
    )
  ));

  getPublicProjectsCount$ = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.GetPublicProjectsPagination>(
      ProjectActions.ActionTypes.GetPublicProjectsPagination
    ),
    mergeMap((action) => {
      const params = {
        filter: {
          fields: {
            id: true,
            categoryIds: true
          }
        }
      } as any;
      return this.projectApi.findPublicProjects(this.handleGetPublicProjectsFilter(params.filter, action.payload.searchText, action.payload.categoryIds, true)).pipe(
        mergeMap(publicProjects => {
          const publicProjectCategoryIds = publicProjects.reduce((acc, page) => {
            if (page.categoryIds) {
              acc.push(...page.categoryIds);
            }
            return acc;
          }, []);
          const actions = [];
          if (!action.payload.searchText && !action.payload.categoryIds) {
            actions.push(new ProjectActions.GetPublicProjectsCategoryIds({ publicProjectCategoryIds }));
          }
          actions.push(new ProjectActions.GetPublicProjectsPaginationSuccessful({ pageIndex: action.payload.pageIndex, pageSize: action.payload.pageSize, searchText: action.payload.searchText, count: publicProjects.length, categoryIds: action.payload.categoryIds, isDynamicPagination: true }));
          return actions;
        }),
        catchError(error => [
          new ProjectActions.ProjectError({ error, errorOn: 'GetPublicProjectsFailure' }),
        ])
      );
    })
  ));

  getPublicProjects$ = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.GetPublicProjectsPaginationSuccessful | ProjectActions.GetPublicProjects | any>(
      ProjectActions.ActionTypes.GetPublicProjectsPaginationSuccessful,
      ProjectActions.ActionTypes.GetPublicProjects,
      AdminProjectsActions.ActionTypes.UpdateProjectByAdminSuccessful
    ),
    mergeMap((action) => {
      const params = {
        filter: {
          skip: this.paginationService.
            getSkippedSizeByPagination(action.payload.pageIndex, action.payload.pageSize),
          limit: action.payload.pageSize
        }
      };
      return this.projectApi.findPublicProjects(this.handleGetPublicProjectsFilter(params.filter, action.payload.searchText, action.payload.categoryIds, action.payload.isDynamicPagination)).pipe(
        map(publicProjects => new ProjectActions.GetPublicProjectsSuccessful({ publicProjects })
        ),
        catchError(error => [
          new ProjectActions.GetPublicProjectsFailure({ payload: { error, key: 'GetPublicProjectsFailure', timestamp: Date.now() } })
        ])
      );
    })
  ));

  getPersonProjectByProjectId = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.GetPersonProjectByProjectId>(
      ProjectActions.ActionTypes.GetPersonProjectByProjectId
    ),
    withLatestFrom(
      this.store.select(state => state.auth.activePerson?.id),
    ),
    mergeMap(([action, activePersonId]) => {
      const params = {
        filter: {
          where: {
            personId: activePersonId,
            projectId: action.payload.projectId
          }
        }
      };
      return this.personProjectApi.find(params).pipe(
        map((personProjects: PersonProjectWithRelations[]) => {
          if (personProjects?.length) {
            const personProject = personProjects[0];
            return new ProjectActions.GetPersonProjectByProjectIdSuccessful({
              personProject: personProject
            });
          } else {
            return new ProjectActions.GetPersonProjectByProjectIdFailure({ error: `Could not find PersonProject with projectId ${action.payload.projectId}` });
          }
        }, catchError(err => [new ProjectActions.GetPersonProjectByProjectIdFailure({ error: err })]))
      );
    })
  ));


  favoritePersonProjects = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.FavoritePersonProject>(
      ProjectActions.ActionTypes.FavoritePersonProject),
    withLatestFrom(
      this.store.select(state => state.project?.data)
    ),
    mergeMap(([action, projects]) => {
      const params = {
        projectId: action.payload.projectId
      };
      const project = projects?.find(p => p.id === action.payload.projectId);
      const projectName = project?.name || 'Project';

      return this.personProjectApi.favorite(params).pipe(
        map(() => {
          this.notificationService.createNotification(
            'success',
            `${projectName}`,
            'This project has been successfully changed to a favorite project'
          );
          return new ProjectActions.FavoritePersonProjectSuccessful({
            projectId: action.payload.projectId, isFavorited: action.payload.isFavorited
          });
        }),
        catchError(error => {
          this.notificationService.createNotification(
            'error',
            'SHARED.ERROR',
            'This project could not be changed to a favorite project'
          );
          return [
            new ProjectActions.FavoritePersonProjectFailure({ payload: { error, key: 'FavoritePersonProjectFailure', timestamp: Date.now() } })
          ];
        })
      );
    })
  ));


  unfavoritePersonProjects = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.UnFavoritePersonProject>(
      ProjectActions.ActionTypes.UnFavoritePersonProject),
    withLatestFrom(
      this.store.select(state => state.project?.data)
    ),
    mergeMap(([action, projects]) => {
      const params = {
        projectId: action.payload.projectId
      };
      const project = projects?.find(p => p.id === action.payload.projectId);
      const projectName = project?.name || 'Project';

      return this.personProjectApi.unfavorite(params).pipe(
        map(() => {
          this.notificationService.createNotification(
            'success',
            `${projectName}`,
            'This project has been successfully changed to a non-favorite project'
          );
          return new ProjectActions.UnFavoritePersonProjectSuccessful({
            projectId: params.projectId, isFavorited: action.payload.isFavorited
          });
        }),
        catchError(error => {
          this.notificationService.createNotification(
            'error',
            'SHARED.ERROR',
            'This project could not be changed to a non-favorite project'
          );
          return [
            new ProjectActions.UnFavoritePersonProjectFailure({ payload: { error, key: 'UnFavoritePersonProjectFailure', timestamp: Date.now() } })
          ];
        })
      );
    })
  ));


  getTotalCount$ = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.GetTotalCount>(
      ProjectActions.ActionTypes.GetTotalCount
    ),
    withLatestFrom(
      this.store.select(state => state.auth.activePerson?.id),
      this.store.select(state => state.project.pagination)
    ),
    mergeMap(([action, activePersonId, pagination]) => {
      const where = {
        personId: activePersonId
      };
      return this.projectApi.count({ where }).pipe(
        map((res: { count: number }) => new ProjectActions.SetPagination({
          pagination: {
            totalCount: res.count,
            totalPageNumber: this.paginationService.getTotalPageNumber(res.count, pagination.pageSize)
          }
        }))
      );
    }),
    catchError(error => [
      new ProjectActions.ProjectError({ errorOn: 'GetTotalCount', error: error }),
    ])
  ));

  /**
   * Get Projects
   *
   * @memberof ProjectsEffects
   */

  getProjects$ = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.GetProjects>(
      ProjectActions.ActionTypes.GetProjects,
      AuthenticationActions.ActionTypes.AcceptInvitationByTokenSuccessful
    ),
    mergeMap((action) => this.projectApi.find().pipe(
      mergeMap((projects: Project[]) => {
        if (projects) {
          return [
            new ProjectActions.SetProjects({ projects: projects })
          ];
        } else {
          this.notificationService.createNotification(
            'error',
            'SHARED.ERROR',
            'PROJECT_MODULE.PROJECT_NOTIFICATIONS.PROJECT_COULDNOT_LOADED'
          );
          return [new ProjectActions.SetProjects({ projects: [] })];
        }
      }),
      catchError(error => [
        new ProjectActions.ProjectError({ errorOn: 'GetProjects', error: error }),
      ])
    ))
  ));

  autoSelectActiveProject$ = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.SetProjects>(
      ProjectActions.ActionTypes.SetProjects
    ),
    withLatestFrom(
      this.store.select(state => state.activeProject.data?.id),
      this.store.select(state => state.auth?.permissions)
    ),
    mergeMap(([action, activeProjectId, permissions]) => {
      const actions: Action[] = [];
      const selectedActiveProject = action.payload.projects?.find(project => project.id === activeProjectId);
      if (selectedActiveProject) {
        // If the active project is already selected, do nothing
        return [];
      } else {
        /* If the active project is not selected, THEN run the following logic to select a project
         * 1 - Set active project if there is only 1 project; ELSE,
         * 2 - Set active project by using the project id from localStorage if exist; ELSE,
         * 3 - Navigate to the latest project, sort by created date and get the latest project;
         * 4 - If there is no project Navigate to project list page
         */
        /* 1 - Set active project if there is only 1 project */
        if (action.payload.projects?.length === 1) {
          const singleProject = action.payload.projects[0];
          actions.push(new SelectActiveProject({ projectId: singleProject.id }));
        } else if (action.payload.projects?.length > 1) {
          // 2 - Set active project by selected active project id from localStorage if exist
          /* set active project by selected active project id from localStorage if exist */
          const activeProjectIdInLocalStorage = this.browserStorageManagementService.getLocalStorageItem('activeProjectId');
          const checkPersonHasProject = activeProjectIdInLocalStorage &&
            action.payload.projects?.some(project => project.id === activeProjectIdInLocalStorage);
          const isAdmin = Array.isArray(permissions) && permissions.includes(UserRoles.Admin);
          // if person has more than 1 project and the project id is in local storage exist in the project list OR user is an admin
          if (checkPersonHasProject || isAdmin) {
            actions.push(new SelectActiveProject({ projectId: activeProjectIdInLocalStorage }));
          } else {
            // if person has more than 1 project and active project id is not exist in local storage
            // THEN navigate to the latest project, sort by created date and get the latest project
            const latestProject = lodash.orderBy(action.payload.projects, ['createdDate'], ['desc'])[0];
            if (latestProject) {
              actions.push(new SelectActiveProject({ projectId: latestProject?.id }));
            }
          }
        } else {
          /* navigate to project list page to select any project */
          actions.push(new Navigate({ url: PATH_DEFINITIONS.PROJECTS.PROJECT_LIST_PATH }));
        }

      }
      return actions;
    })
  ));


  setPagination$ = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.SetPagination>(
      ProjectActions.ActionTypes.SetPagination
    ),
    mergeMap(action => {
      if (action.payload.getDataAfter) {
        return of(new ProjectActions.GetProjects());
      } else {
        return [new ProjectActions.SetPaginationFailure({ error: 'There is no getDataAfter in payload', key: 'SetPaginationFailure', timestamp: Date.now() })];
      }
    })
  ));

  /**
   * Create Project
   *
   * @memberof ProjectsEffects
   */

  createProject$ = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.CreateProject>(
      ProjectActions.ActionTypes.CreateProject
    ),
    concatMap(action => {
      const projectData = {
        ...action.payload.project,
        metadata: action.payload.project.metadata ?? []
      };
      return this.projectApi.create_v2({ body: projectData }).pipe(
        mergeMap((project: Project) => {
          const actions: Action[] = [
            new ProjectActions.CreateProjectSuccessful({ project: project }),
            // Change to the new project
            new SelectActiveProject({
              projectId: project.id,
              navigateToProjectDetail: true,
              navigateAIAssistantPage: false
            })
          ];
          this.notificationService.createNotification(
            'success',
            'Your project has been successfully created',
            'It is ready for further development. Use builder tools on the left menu and get help from our AI to enhance its features.',
            { nzDuration: environment.successNotificationVisibilityDuration }
          );

          return actions;
        }),
        catchError(error => {
          const errorMessage = error?.error?.error?.message;
          this.notificationService.createNotification(
            'error',
            'SHARED.ERROR',
            errorMessage
          );
          return [
            new ProjectActions.CreateProjectFailure(),
            new ProjectActions.ProjectError({ errorOn: 'CreateProject', error: error }),
          ];
        })
      );
    })
  ));

  /**
   * Update project
   *
   * @memberof ProjectEffects
   */
  updateProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectActions.UpdateProject>(
        ProjectActions.ActionTypes.UpdateProject
      ),
      withLatestFrom(
        this.store.select((state) => state.auth.activePerson),
        this.store.select((state) => state.activeProject.data?.id)
      ),
      concatMap(([action, person, activeProjectId]) => {
        const result: Observable<Action>[] = [];

        // Optimistic update action
        result.push(
          of(
            new ProjectActions.UpdateProjectSuccessful({
              project: action.payload.project,
              projectId: action.payload.projectId,
            })
          )
        );

        const body: ProjectUpdateDtoPartial = {
          ...action.payload.project
        };

        if (action.payload.project) {
          result.push(
            this.projectApi.updateById({
              id: action.payload.projectId,
              body,
            }).pipe(
              map((project) => {
                if (project) {
                  this.notificationService.createNotification(
                    'success',
                    'SHARED.SUCCESSFUL',
                    action.payload.notificationMessageOnSuccess ||
                    'PROJECT_MODULE.PROJECT_NOTIFICATIONS.PROJECT_UPDATED',
                    {
                      nzPlacement: action.payload.notificationPlacement || 'bottomRight',
                    }
                  );
                  return new ProjectActions.UpdateProjectSuccessful({
                    project: project,
                    projectId: action.payload.projectId,
                  });
                } else {
                  this.notificationService.createNotification(
                    'error',
                    'SHARED.ERROR',
                    action.payload.notificationMessageOnSuccess ||
                    'PROJECT_MODULE.PROJECT_NOTIFICATIONS.PROJECT_COULDNOT_UPDATED',
                    {
                      nzPlacement: action.payload.notificationPlacement || 'bottomRight',
                    }
                  );
                  return new ProjectActions.UpdateProjectFailure({
                    error: 'Project could not be updated',
                    key: 'UpdateProjectFailure',
                    timestamp: Date.now(),
                  });
                }
              }),
              catchError((error) =>
                of(
                  new ProjectActions.UpdateProjectFailure({
                    error: 'Project could not be updated',
                    key: 'UpdateProjectFailure',
                    timestamp: Date.now(),
                  })
                )
              )
            )
          );
        }
        if (action.payload.redirectToProjectDetail !== false) {
          result.push(
            of(
              new Navigate({
                url: `${PATH_DEFINITIONS.PROJECTS.PROJECT_DETAIL_PATH}/${activeProjectId}`,
              })
            )
          );
        }

        return concat(...result);
      })
    )
  );


  /**
   * Delete Project
   *
   * @memberof ProjectsEffects
   */

  deleteProject$ = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.DeleteProject>(
      ProjectActions.ActionTypes.DeleteProject
    ),
    mergeMap((action) => {
      /* get projectId by action payload */
      const projectId = action.payload.projectId;
      /* check projectId is specified */
      if (!projectId) {
        return [new ProjectActions.DeleteProjectFailure({ error: 'There is no projectId', key: 'DeleteProjectFailure', timestamp: Date.now() })];
      }
      /* delete project */
      return this.projectApi.deleteById({ projectId: action.payload.projectId }).pipe(
        map(() => {
          this.notificationService.createNotification(
            'success',
            'SHARED.SUCCESSFUL',
            'PROJECT_MODULE.PROJECT_NOTIFICATIONS.PROJECT_DELETED'
          );
          this.browserStorageManagementService.removeLocalStorageItem('activeProjectId');
          return new ProjectActions.DeleteProjectSuccessful({ projectId: action.payload.projectId });
        }),
        catchError(error => {
          const errorObject = JSON.parse(error.error);
          this.notificationService.createNotification(
            'error',
            'SHARED.ERROR',
            errorObject?.error?.message || 'PROJECT_MODULE.PROJECT_NOTIFICATIONS.PROJECT_COULDNOT_DELETED'
          );
          return [
            new ProjectActions.ProjectError({ errorOn: ProjectActions.ActionTypes.DeleteProject, error: error })
          ];
        })
      );
    })
  ));


  setAsActiveProjectTheme$ = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.SetAsActiveProjectTheme>(
      ProjectActions.ActionTypes.SetAsActiveProjectTheme
    ),
    withLatestFrom(
      this.store.select(state => state.projectTheme?.data)
    ),
    mergeMap(([action, projectThemes]) => {
      const params = {
        id: action.payload.activeProjectId,
        body: {
          activeProjectThemeId: action.payload.projectThemeId
        }
      };

      const themeName = projectThemes?.find(theme => theme.id === action.payload.projectThemeId)?.name || 'Unknown Theme';

      return this.projectApi.updateById(params).pipe(
        mergeMap(() => {
          this.notificationService.createNotification(
            'success',
            `${themeName}`,
            'Project theme successfully set to active'
          );
          return [
            new ProjectActions.SetAsActiveProjectThemeSuccessful({
              projectThemeId: action.payload.projectThemeId,
              activeProjectId: action.payload.activeProjectId,
              themeName: themeName
            })
          ];
        }),
        catchError(error => {
          this.notificationService.createNotification(
            'error',
            `${themeName}"`,
            'Project theme could not set to active'
          );
          return [
            new ProjectActions.ProjectError({ errorOn: ProjectActions.ActionTypes.SetAsActiveProjectTheme, error: error })
          ];
        })
      );
    })
  ));

  copyProject$ = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.CopyProject>(
      ProjectActions.ActionTypes.CopyProject
    ),
    withLatestFrom(
      this.store.select(state => state.activeProject.data?.id),
      this.store.select(selectPublicProjects)
    ),
    mergeMap(([action, activeProjectId, publicProjects]) => {
      const targetProjectId = action.payload.targetProjectId || activeProjectId;

      if (!targetProjectId) {
        this.notificationService.createNotification(
          'error',
          'Error',
          'Active project not found. Please select a project first.'
        );
        return of(new ProjectActions.CopyProjectFailure({
          error: 'No target project ID available',
          key: 'CopyProjectFailure',
          timestamp: Date.now()
        }));
      }

      if (!action.payload.copyProjectId) {
        this.notificationService.createNotification(
          'error',
          'Error',
          'Source module ID is missing.'
        );
        return of(new ProjectActions.CopyProjectFailure({
          error: 'No source project ID provided',
          key: 'CopyProjectFailure',
          timestamp: Date.now()
        }));
      }

      const sourceModule = publicProjects?.find(project => project.id === action.payload.copyProjectId);
      const moduleName = sourceModule?.name || sourceModule?.title || 'Module';

      return this.projectApi.copyProject({
        projectId: action.payload.copyProjectId
      }).pipe(
        mergeMap(() => {
          this.notificationService.createNotification(
            'success',
            `${moduleName}`,
            'Module has been successfully copied to your project'
          );

          const actions = [
            new ProjectActions.CopyProjectSuccessful({
              copiedProject: { id: action.payload.copyProjectId } as any,
              targetProjectId: targetProjectId
            })
          ];

          this.syncRemoteDataService.syncRemoteDataItemWithKey(targetProjectId, StoreKeys.ActiveProject);

          return actions;
        }),
        catchError(error => {
          const errorMessage = error?.error?.error?.message || `${moduleName} could not be copied to your project`;
          this.notificationService.createNotification(
            'error',
            'Error',
            errorMessage
          );

          return of(new ProjectActions.CopyProjectFailure({
            error: error,
            key: 'CopyProjectFailure',
            timestamp: Date.now()
          }));
        })
      );
    })
  ));

  removeCopiedProject$ = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.RemoveCopiedProject>(
      ProjectActions.ActionTypes.RemoveCopiedProject
    ),
    withLatestFrom(
      this.store.select(state => state.activeProject.data?.id),
      this.store.select(state => state.activeProject.data?.copiedProjects || [])
    ),
    mergeMap(([action, activeProjectId, copiedProjects]) => {
      const targetProjectId = action.payload.targetProjectId || activeProjectId;

      if (!targetProjectId) {
        this.notificationService.createNotification(
          'error',
          'Error',
          'Active project not found. Please select a project first.'
        );
        return of(new ProjectActions.RemoveCopiedProjectFailure({
          error: 'No target project ID available',
          key: 'RemoveCopiedProjectFailure',
          timestamp: Date.now()
        }));
      }

      if (!action.payload.copyProjectId) {
        this.notificationService.createNotification(
          'error',
          'Error',
          'Module ID is missing.'
        );
        return of(new ProjectActions.RemoveCopiedProjectFailure({
          error: 'No copy project ID provided',
          key: 'RemoveCopiedProjectFailure',
          timestamp: Date.now()
        }));
      }

      const sourceModule = copiedProjects?.find(project => project.id === action.payload.copyProjectId);
      const moduleName = sourceModule?.name || sourceModule?.title || 'Module';

      return this.projectApi.removeCopiedProject({
        projectId: action.payload.copyProjectId
      }).pipe(
        mergeMap(() => {
          this.notificationService.createNotification(
            'success',
            `${moduleName}`,
            'Module has been successfully deleted from your project'
          );

          const actions = [
            new ProjectActions.RemoveCopiedProjectSuccessful({
              removedProjectId: action.payload.copyProjectId,
              targetProjectId: targetProjectId
            })
          ];

          this.syncRemoteDataService.syncRemoteDataItemWithKey(targetProjectId, StoreKeys.ActiveProject);

          return actions;
        }),
        catchError(error => {
          const errorMessage = error?.error?.error?.message || `${moduleName} could not be deleted from your project`;
          this.notificationService.createNotification(
            'error',
            'Error',
            errorMessage
          );

          return of(new ProjectActions.RemoveCopiedProjectFailure({
            error: error,
            key: 'RemoveCopiedProjectFailure',
            timestamp: Date.now()
          }));
        })
      );
    })
  ));

  /**
   * Create Module Project
   *
   * @memberof ProjectsEffects
   */

  createModuleProject$ = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.CreateModuleProject>(
      ProjectActions.ActionTypes.CreateModuleProject
    ),
    concatMap(action => {
      const moduleProjectData = {
        name: action.payload.project.name,
        key: action.payload.project.key,
        description: action.payload.project.description,
        categoryIds: action.payload.project.categoryIds,
        isCreatedByAI: false,
        appType: 'web',
        frontendFramework: 'angular',
        backendFramework: 'loopback4'
      };
      return this.projectApi.createModuleProject({ body: moduleProjectData }).pipe(
        mergeMap((project: Project) => {
          const actions: Action[] = [
            new ProjectActions.CreateModuleProjectSuccessful({ project: project }),
            new SelectActiveProject({
              projectId: project.id,
              navigateToProjectDetail: true,
              navigateAIAssistantPage: false
            })
          ];
          const moduleName = project.name;
          this.notificationService.createNotification(
            'success',
            `${moduleName}`,
            'Your module project has been successfully created',
          );

          return actions;
        }),
        catchError(error => {
          const errorMessage = error?.error?.error?.message || 'Module project could not be created';
          this.notificationService.createNotification(
            'error',
            'SHARED.ERROR',
            errorMessage
          );
          return [
            new ProjectActions.CreateModuleProjectFailure({
              payload: { error: error, key: 'CreateModuleProjectFailure', timestamp: Date.now() }
            }),
            new ProjectActions.ProjectError({ errorOn: 'CreateModuleProject', error: error }),
          ];
        })
      );
    })
  ));

  setupDatabase = createEffect(() => this.actions$.pipe(
    ofType<ProjectActions.SetupDatabase>(
      ProjectActions.ActionTypes.SetupDatabase
    ),
    mergeMap(() =>
      this.projectApi.setupDatabase().pipe(
        map(() => {
          this.notificationService.createNotification(
            'success',
            'Successful',
            'Your database has been deployed successfully.'
          );
          return new ProjectActions.SetupDatabaseSuccessful()
        }),
        catchError(error => {
          this.notificationService.createNotification(
            'error',
            'SHARED.ERROR',
            'Database creation failed'
          );
          return [
            new ProjectActions.SetupDatabaseFailure({ payload: { error, key: 'SetupDatabaseFailure', timestamp: Date.now() } })
          ];
        })
      )
    )
  ));

  private handleGetPublicProjectsFilter(filter, searchText, categoryIds, isDynamicPagination) {
    const params = {
      filter: filter
    } as any;

    if (searchText && searchText !== '' && isDynamicPagination) {
      params.filter.where = {
        ...params.filter.where,
        name: { regexp: `/${searchText}/i` }
      };
    }
    if (categoryIds?.length && isDynamicPagination) {
      params.filter.where = {
        ...params.filter.where,
        categoryIds: { inq: categoryIds }
      };
    }
    return params;
  }
}
